import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

export type TourStatus = "pending" | "skipped" | "completed";

const storageKey = (uid: string) => `gfi_tour_${uid}`;

export function useTour() {
  const { user } = useAuth();
  const [status, setStatus] = useState<TourStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    // Fast path: localStorage first to avoid flash
    const cached = localStorage.getItem(storageKey(user.id)) as TourStatus | null;
    if (cached) {
      setStatus(cached);
      setLoading(false);
    }

    // DB é authoritative — sincroniza para cross-device
    supabase
      .from("user_onboarding")
      .select("tour_status")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.tour_status) {
          // Linha existe no DB → DB é autoridade (cross-device wins)
          const dbStatus = data.tour_status as TourStatus;
          setStatus(dbStatus);
          localStorage.setItem(storageKey(user.id), dbStatus);
        }
        // Se DB não retornou linha, respeitar o cache local:
        // upsert pode estar em voo (race entre write e read).
        // Novo usuário sem cache local: mantém status=null → shouldShowTour=true.
        if (!cached) setLoading(false);
      });
  }, [user?.id]);

  const persist = useCallback(
    async (next: TourStatus) => {
      if (!user) return;
      // Optimistic local update
      setStatus(next);
      localStorage.setItem(storageKey(user.id), next);
      // DB upsert (background)
      const { error } = await supabase.from("user_onboarding").upsert(
        {
          user_id: user.id,
          tour_status: next,
          ...(next === "pending" ? { started_at: new Date().toISOString(), completed_at: null } : {}),
          ...(next === "completed" ? { completed_at: new Date().toISOString() } : {}),
        },
        { onConflict: "user_id" }
      );
      // Falhar alto. O erro deste upsert era descartado: uma gravação que falha
      // deixa o cache local dizendo "visto" e o banco dizendo "pendente", e o
      // tour reabre em todo outro dispositivo sem que nada apareça em lugar nenhum.
      if (error) {
        throw new Error(`Falha ao gravar o status do tour ("${next}"): ${error.message}`);
      }
    },
    [user?.id]
  );

  return {
    status,
    loading,
    shouldShowTour: !loading && (status === "pending" || status === null),
    markSkipped: () => persist("skipped"),
    markCompleted: () => persist("completed"),
    resetTour: () => persist("pending"),
  };
}
